import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useCompanySummaryState } from '../../hooks';
import { useTracking } from 'react-tracking';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  VALUATION_PRICE_VS_FAIR_RATIO,
  VALUATION_PE_RATIO,
  COMPANY_ABOUT_ANCHOR,
  VALUATION_RATIO_VS_PEERS,
  ANALYST_PRICE_TARGETS,
} from '@/constants/anchors';
import { COMPANY_SNOWFLAKE } from '@/constants/feedbackSections';
import { SnowflakeType } from '@/components/Snowflake/SnowflakeType';
import {
  SnowflakeChart,
  RiskRewardWrapper,
  AboutIcon,
  AboutCompanyButton,
  SummarySection,
  OverviewSectionHeader,
  Description,
  SnowflakeTitle,
  SnowflakeCaption,
  SnowflakeDescription,
} from './styled';
import RiskRewardLoadingSkeleton from '../RiskRewardLoadingSkeleton';
import { Button, Grid } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import SnowflakeChartWrapper from '../SnowflakeChartWrapper';
import SnowflakeTable from '../SnowflakeTable';
import RiskReward from '../RiskReward';
import RiskSummaryList from '../RiskSummaryList';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import RiskRewardHeading from '../RiskRewardHeading';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import { isValidSection } from '@/pages/CompanyReport/useSection';
import { SUMMARY_STATEMENTS } from '../../constants';
import useCompanyStatementsGQLFormatted from '@/hooks/useCompanyStatementsGQLFormatted';

const RiskCheckModal = loadable(() => import('../RiskCheckModal'));

const Snowflake = loadable(() => import('@/components/Snowflake/Snowflake'));

const routes: string[] = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

export function Summary() {
  const {
    isFund,
    shortDescription,
    snowflakePoints,
    uniqueSymbol,
    tickerSymbol,
    hasRewards,
    summaryRiskStatus,
    firstFourFailingRisks,
    allRisks,
    totalFailingRisks,
    fetchStatementsStatus,
    fundamentalsSentence,
    companyName,
    rewardsStatements: {
      isGoodValue,
      isCompanyGrowing,
      hasCompanyBeenGrowing,
      isDividendAttractive,
    },
    analysisChecks,
  } = useCompanySummaryState();
  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsGQLFormatted({
    canonicalURL: canonicalUrl,
    names: SUMMARY_STATEMENTS,
  });
  const { trackEvent } = useTracking({ type: 'track', subject: 'summary' });
  const [openRisksModal, setOpenRiskModal] = useState(false);
  const { loadSection } = useVisibleSectionsContext();

  useEffect(() => {
    trackEvent({
      action: 'landed',
    });
  }, [trackEvent]);

  const handleSnowflakeClick = useCallback<
    NonNullable<ComponentPropsWithoutRef<typeof Snowflake>['handleClick']>
  >(
    (route) => {
      if (isValidSection(route)) {
        loadSection(route);
      } else {
        console.warn('attempted to redirect to an invalid section:', route);
      }
    },
    [loadSection]
  );

  return (
    <SummarySection data-cy-id="company-summary">
      <Grid
        columnGap="8px"
        templateRows="auto 1fr"
        templateColumns="1fr 1fr"
        templateColumnsSm="1fr 200px"
        templateColumnsMd="1fr 280px"
      >
        <div>
          <OverviewSectionHeader>
            {tickerSymbol} Stock Overview
          </OverviewSectionHeader>
          <Description
            isEmptyState={!shortDescription}
            data-cy-id="company-summary-desc"
          >
            {shortDescription ||
              (fetchStatementsStatus === 'success' &&
                'No business description')}
          </Description>
          <AboutCompanyButton
            styleType="strongLink"
            data-cy-id="company-about-button"
            onClick={() => {
              window.location.replace(`#${COMPANY_ABOUT_ANCHOR}`);
              trackEvent({
                modifier: 'about-company-read-more',
                action: 'click',
              });
            }}
          >
            <AboutIcon />
            About the company
          </AboutCompanyButton>
        </div>
        <Grid row="span 1" rowSm="span 2">
          <div>
            <SnowflakeChartWrapper
              id="overview-summary-snowflake"
              points={snowflakePoints || [0, 0, 0, 0, 0]}
              companyForFeedback={uniqueSymbol}
              feedbackLabel={COMPANY_SNOWFLAKE}
              chart={
                <SnowflakeChart data-cy-id="overview-summary-snowflake">
                  <Snowflake
                    points={snowflakePoints}
                    detailedPoints={analysisChecks}
                    tooltip={true}
                    routes={routes}
                    handleClick={handleSnowflakeClick}
                    disabled={isFund}
                    type={SnowflakeType.COMPANY}
                  />
                  <SnowflakeDescription>
                    <SnowflakeTitle>Snowflake Analysis</SnowflakeTitle>
                    {fundamentalsSentence && !isFund && (
                      <SnowflakeCaption data-cy-id="company-fundamentals-desc">
                        {fundamentalsSentence}
                      </SnowflakeCaption>
                    )}
                  </SnowflakeDescription>
                </SnowflakeChart>
              }
              table={
                <SnowflakeChart>
                  <SnowflakeTable
                    points={snowflakePoints || [0, 0, 0, 0, 0]}
                    tickerSymbol={tickerSymbol}
                  />
                </SnowflakeChart>
              }
            />
          </div>
        </Grid>
        <Grid column="span 2" columnSm="span 1">
          <RiskRewardWrapper data-cy-id="risk-reward-wrapper">
            {fetchStatementsStatus === 'success' ? (
              <>
                {hasRewards && !isFund && (
                  <>
                    <RiskRewardHeading>Rewards</RiskRewardHeading>
                    {isGoodValue.value === 'Pass' && (
                      <RiskReward
                        name="isGoodValue"
                        index={0}
                        hash={
                          isGoodValue.outcomeName === 'OUTCOME_TRUE'
                            ? VALUATION_PRICE_VS_FAIR_RATIO
                            : VALUATION_PE_RATIO
                        }
                        statementId={isGoodValue.condensedTitle}
                        description={isGoodValue.description}
                      />
                    )}
                    {isCompanyGrowing.value === 'Pass' && (
                      <RiskReward
                        name="isCompanyGrowing"
                        index={1}
                        hash={COMPANY_FUTURE_ANCHOR}
                        statementId={isCompanyGrowing.condensedTitle}
                        description={isCompanyGrowing.description}
                      />
                    )}
                    {hasCompanyBeenGrowing.value === 'Pass' && (
                      <RiskReward
                        name="hasCompanyBeenGrowing"
                        index={2}
                        hash={COMPANY_PAST_ANCHOR}
                        statementId={hasCompanyBeenGrowing.condensedTitle}
                        description={hasCompanyBeenGrowing.description}
                      />
                    )}
                    {isDividendAttractive.value === 'Pass' && (
                      <RiskReward
                        name="isDividendAttractive"
                        index={3}
                        hash={COMPANY_DIVIDEND_ANCHOR}
                        statementId={isDividendAttractive.condensedTitle}
                        description={isDividendAttractive.description}
                      />
                    )}
                    {/* Following rewards are fetched from gql to be consistent with their chart */}
                    {statements?.IsGoodRelativeValue?.value === true && (
                      <RiskReward
                        name="isGoodRelativeValue"
                        index={4}
                        hash={VALUATION_RATIO_VS_PEERS}
                        statementId={statements.IsGoodRelativeValue?.name}
                        description={
                          statements.IsGoodRelativeValue?.description
                            ?.description
                        }
                      />
                    )}
                    {statements?.IsTradingBelowAnalystPriceTargets?.value ===
                      true && (
                      <RiskReward
                        name="isTradingBelowAnalystPriceTargets"
                        index={7}
                        hash={ANALYST_PRICE_TARGETS}
                        statementId={
                          statements.IsTradingBelowAnalystPriceTargets?.name
                        }
                        description={
                          statements.IsTradingBelowAnalystPriceTargets
                            ?.description?.description
                        }
                      />
                    )}
                  </>
                )}
                {!isFund && (
                  <>
                    <RiskRewardHeading>Risk Analysis</RiskRewardHeading>
                    <RiskSummaryList
                      summaryRiskStatus={summaryRiskStatus}
                      firstFourFailingRisks={firstFourFailingRisks}
                      totalFailingRisks={totalFailingRisks}
                      tickerSymbol={tickerSymbol}
                    />
                    <Button
                      displayXsDown="block"
                      onClick={() => {
                        setOpenRiskModal(true);
                        trackEvent({
                          modifier: 'see-all-risk-checks',
                          action: 'click',
                        });
                      }}
                      styleType="loud"
                      data-cy-id="summary-risk-modal-button"
                    >
                      See All Risk Checks
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <RiskRewardHeading>Rewards</RiskRewardHeading>
                <RiskRewardLoadingSkeleton data-cy-id="reward-loading-skeleton" />
                <RiskRewardHeading>Risk Analysis</RiskRewardHeading>
                <RiskRewardLoadingSkeleton data-cy-id="risk-loading-skeleton" />
              </>
            )}
            {openRisksModal && (
              <RiskCheckModal
                companyShortName={companyName}
                companyTickerSymbol={tickerSymbol}
                riskSummaryList={allRisks}
                onClose={() => setOpenRiskModal(false)}
              />
            )}
          </RiskRewardWrapper>
        </Grid>
      </Grid>
    </SummarySection>
  );
}
